import { ProductCarouselProductStyles } from "@dana/Components/DanaProductCarouselProduct";
import NextArrowIcon from "@dana/Icons/NextArrowIcon";
import PrevArrowIcon from "@dana/Icons/PrevArrowIcon";
import { elevation, themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { getResponsiveSpacing } from "@dana/MobiusOverrides/styles/spacing";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import Img, { ImgProps } from "@insite/mobius/Img";
import Link, { LinkPresentationProps, StyledTypography } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useEmblaCarousel from "embla-carousel-react";
import React, { createRef, useCallback, useEffect, useState } from "react";
import { css, ThemeProps, withTheme } from "styled-components";

// Define the structure for each document card
interface DocumentCard {
    fields: {
        image: string;
        title: string;
        documentUrl: LinkFieldValue;
    };
}

const enum fields {
    documents = "documents",
}

// Define the shape of the data the widget will receive
interface ProductDocumentCarouselWidgetProps extends WidgetProps {
    fields: {
        [fields.documents]: DocumentCard[];
    };
}

type Props = ProductDocumentCarouselWidgetProps & ThemeProps<BaseTheme> & HasShellContext;

export interface ProductDocumentCarouselStyles {
    productCarouselContainer?: InjectableCss;
    errorMessageText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    carouselArrowWrapper?: InjectableCss;
    prevArrowGridItem?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: LinkPresentationProps;
    documentTitle?: TypographyPresentationProps;
    documentImage?: ImgProps;
}

export const productCarouselStyles: ProductDocumentCarouselStyles = {
    productCarouselContainer: {
        css: css`
            max-width: 1440px;
            margin: 0 auto;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 40px 16px;
                        `,
                        css`
                            padding: 0 16px;
                        `,
                        css`
                            padding: 0 24px;
                        `,
                        css`
                            padding: 0 40px;
                        `,
                    ],
                    "min",
                )}
            ${getResponsiveSpacing("py", "xLarge")};
        `,
    },
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    mainContainer: { gap: 0 },
    carouselArrowWrapper: {
        css: css`
            width: 100%;
            max-width: 1440px;
            margin: 0 auto;
            margin-bottom: 16px;
            display: flex;
            column-gap: 8px;
            justify-content: flex-end;
        `,
    },
    prevArrowGridItem: {
        css: css`
            justify-content: flex-start;
        `,
    },
    prevArrowButton: {
        shape: "rectangle",
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            padding: 0;
            min-width: auto;
            min-height: auto;
            z-index: 2;
            justify-content: center;
            background: none;
            border: none;

            ${IconWrapper} {
                svg {
                    path {
                        fill: ${themeColors.secondary.danaDarkBlue};
                    }
                }
            }

            &:not(:disabled) {
                &:hover {
                    background: transparent;
                    border: none;

                    ${IconWrapper} {
                        svg {
                            path {
                                fill: ${themeColors.secondary.danaBrightBlue};
                            }
                        }
                    }
                }

                &:focus {
                    outline: none;
                }

                &:focus-visible {
                    background: transparent;
                    border: 1px solid ${themeColors.primary.white};
                    box-shadow: ${elevation.focus};
                    outline: none;

                    ${IconWrapper} {
                        svg {
                            path {
                                fill: ${themeColors.secondary.danaBrightBlue};
                            }
                        }
                    }
                }
            }

            &:disabled {
                background: none;
                border: none;
                ${IconWrapper} {
                    svg {
                        path {
                            fill: ${themeBackgrounds.darkGray};
                        }
                    }
                }
            }
        `,
    },
    nextArrowGridItem: {
        css: css`
            justify-content: flex-end;
        `,
    },
    nextArrowButton: {
        shape: "rectangle",
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            padding: 0;
            min-width: auto;
            min-height: auto;
            z-index: 2;
            justify-content: center;
            background: none;
            border: none;

            ${IconWrapper} {
                svg {
                    path {
                        fill: ${themeColors.secondary.danaDarkBlue};
                    }
                }
            }

            &:not(:disabled) {
                &:hover {
                    background: transparent;
                    border: none;

                    ${IconWrapper} {
                        svg {
                            path {
                                fill: ${themeColors.secondary.danaBrightBlue};
                            }
                        }
                    }
                }

                &:focus {
                    outline: none;
                }

                &:focus-visible {
                    background: transparent;
                    border: 1px solid ${themeColors.primary.white};
                    box-shadow: ${elevation.focus};
                    outline: none;

                    ${IconWrapper} {
                        svg {
                            path {
                                fill: ${themeColors.secondary.danaBrightBlue};
                            }
                        }
                    }
                }
            }

            &:disabled {
                background: none;
                border: none;
                ${IconWrapper} {
                    svg {
                        path {
                            fill: ${themeBackgrounds.darkGray};
                        }
                    }
                }
            }
        `,
    },
    carouselGridItem: {
        width: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            overflow: hidden;
            padding-bottom: 2px;
            width: 100%;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;

            @media print {
                transform: none !important;
                display: block;
            }
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;

            padding-left: 8px;
            padding-right: 8px;

            @media print {
                width: 50% !important;
                float: left;
                margin-bottom: 16px;
                height: 350px;
            }
        `,
    },
    carouselSlideInner: {
        typographyProps: {
            underline: false,
        },
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 20px;
            display: flex;
            flex-direction: column;

            border: 1px solid ${themeBackgrounds.lightGray};
            padding: 24px;

            &:hover,
            &:focus-visible {
                border-color: ${themeColors.primary.danaBlue};
                box-shadow: ${elevation.hover};
                outline: none;
            }

            text-decoration: none;

            ${StyledTypography} {
                text-decoration: none;
            }

            &:hover {
                text-decoration: none;
                ${StyledTypography} {
                    text-decoration: none;
                }
            }
        `,
    },
    documentTitle: {
        variant: "h4",
        css: css`
            margin-top: 24px;
            text-align: center;
            margin-bottom: 0;
        `,
    },
    documentImage: {
        css: css`
            display: block;
            height: auto;
            width: 100%;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-height: 200px;
                        max-height: 200px;
                        object-fit: contain;
                    `,
                    css`
                        min-height: 200px;
                        max-height: 200px;
                        object-fit: contain;
                    `,
                    css`
                        min-height: 337px;
                        max-height: 337px;
                        object-fit: contain;
                    `,
                    css`
                        min-height: 280px;
                        max-height: 280px;
                        object-fit: contain;
                    `,
                    css`
                        min-height: 280px;
                        max-height: 280px;
                        object-fit: contain;
                    `,
                ])}
        `,
    },
};

const styles = productCarouselStyles;

const ProductDocumentCarouselWidget = ({ fields, shellContext, theme }: Props) => {
    const { documents } = fields;
    const afterCarousel = createRef<HTMLSpanElement>();
    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [slidesToScroll, setSlidesToScroll] = useState(0);

    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = useCallback(() => {
        const maxNumberOfColumns = 4;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        const { innerWidth } = window;
        if (innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else {
            localSlidesToScroll = 4;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, []);

    useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);
    }, [windowResizeTime]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    useEffect(() => {
        if (!embla || !documents || documents.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            watchDrag: isTouchDevice(),
            loop: false,
        });
        setCanScroll();
    }, [embla, slidesToScroll, documents]);

    if (!documents || documents.length === 0) {
        return <Typography>No documents to display.</Typography>;
    }

    return (
        <StyledWrapper className="product-document-carousel" {...styles.productCarouselContainer}>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            <StyledWrapper className="carouselArrowWrapper" {...styles.carouselArrowWrapper}>
                <StyledWrapper className="prevArrowGridItem" {...styles.prevArrowGridItem}>
                    {documents.length > slidesToScroll && (
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            disabled={!canScrollPrev}
                            aria-label={translate("Previous")}
                            data-test-selector="prevBtn"
                        >
                            <ButtonIcon src={PrevArrowIcon} />
                        </Button>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.nextArrowGridItem}>
                    {documents.length > slidesToScroll && (
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            disabled={!canScrollNext}
                            aria-label={translate("Next")}
                            data-test-selector="nextBtn"
                        >
                            <ButtonIcon src={NextArrowIcon} />
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <GridContainer data-test-selector="product-document-carousel" {...styles.mainContainer}>
                <GridItem {...styles.carouselGridItem}>
                    <StyledWrapper {...styles.carouselWrapper}>
                        <StyledWrapper ref={emblaRef} {...styles.carouselContainer}>
                            <StyledWrapper data-test-selector="slides" {...styles.carouselSlidesContainer}>
                                {documents.map((doc, index) => (
                                    <StyledWrapper
                                        {...styles.carouselSlide}
                                        key={`${doc?.fields?.title}-${index}`}
                                        style={{ width: `calc(100% / ${slidesToScroll})` }}
                                    >
                                        <Link
                                            {...styles.carouselSlideInner}
                                            href={useGetLink(doc.fields?.documentUrl)?.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ textDecoration: "none" }}
                                            data-test-selector="carousel_slide"
                                            tabIndex={0}
                                        >
                                            <StyledWrapper className="document-card">
                                                {doc?.fields.image && (
                                                    <Img
                                                        src={doc?.fields.image}
                                                        alt={doc?.fields.title}
                                                        {...styles.documentImage}
                                                    />
                                                )}
                                                <Typography {...styles.documentTitle}>
                                                    {translate(doc.fields.title)}
                                                </Typography>
                                            </StyledWrapper>
                                        </Link>
                                    </StyledWrapper>
                                ))}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withIsInShell(withTheme(ProductDocumentCarouselWidget)),
    definition: {
        group: "Common",
        displayName: "Product Document Carousel",
        fieldDefinitions: [
            {
                name: "documents",
                editorTemplate: "ListField",
                displayName: "Product Documents",
                getDisplay: ({ fields: { title } }) => title || "Slide",
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "Document Title",
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "documentUrl",
                        displayName: "Document URL",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
